import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  FunctionComponent,
} from "react";
import classnames from 'classnames';
import { CSSTransition } from 'react-transition-group';

import { manageDropdownListener, manageKeydownListener } from '@helpers/use_effect_callbacks';
import { isOutOfView } from '@helpers/isInView';
import {
  ActionsOpenDirections,
  DropdownNavigateKeys,
  DropdownTransitions,
  ListOpenDirections,
  ListOptionValue,
  OpenDirections,
  UserDevices,
} from '@components/library/dropdown/index';

import './dropdown_transitions.scss';

interface DropdownProps {
  styleClassNames: { [key: string]: string };
  variantName: string;
  anchorElement?: JSX.Element;
  arrowedItem?: ListOptionValue;
  className?: string;
  closeCallback?: () => void;
  closeDropdown?: boolean;
  dropdownTransition?: DropdownTransitions;
  dropdownTransitionTime?: number;
  keydownCallback?: (key: DropdownNavigateKeys) => void;
  openDirection?: OpenDirections;
  openOnClick?: boolean;
  openOnHover?: boolean;
  overflowParentRef?: MutableRefObject<HTMLElement>;
  setIsDropdownOpen?: SetStateDispatch<boolean>;
  useUlContainer?: boolean;
  userDevice?: UserDevices;
}

enum OpenDirectionOverride {
  Up = 'up',
}

interface MutableRefObject<T> {
  current: T;
}

const baseName = 'dropdown';

const BaseDropdown: FunctionComponent<DropdownProps> = (props) => {
  const {
    anchorElement,
    arrowedItem,
    children,
    className = '',
    styleClassNames,
    variantName,
    dropdownTransition = '',
    dropdownTransitionTime = 300,
    closeDropdown = false,
    closeCallback = undefined,
    keydownCallback,
    openDirection = ActionsOpenDirections.DownLeft,
    openOnClick = true,
    openOnHover = false,
    overflowParentRef,
    setIsDropdownOpen = (): void => { /* noop */ },
    useUlContainer = true,
    userDevice = BFG?.currentUser?.device || UserDevices.Desktop,
  } = props;
  const [showDropdown, setShowDropdown] = useState(false);
  const [openDirectionOverride, setOpenDirectionOverride] = useState<OpenDirectionOverride>(null);
  const [inlineStyles, setInlineStyles] = useState({});
  const [storedTimeouts, setStoredTimeouts] = useState([]);

  const menuRef = useRef(null);
  const contentContainerRef = useRef(null);

  const cancelStoredTimeouts = (): void => {
    storedTimeouts.forEach((storedTimeout) => clearTimeout(storedTimeout));
  };

  const isTouchScreen = userDevice === UserDevices.Mobile || userDevice === UserDevices.Tablet;

  const handleOpenAndClose = (e: any): void => {
    switch (e.type) {
      case 'keydown':
        // toggle the menu open/closed, but if a dropdown item is targeted (highlighted via arrow
        // keys) defer to manageKeydownListener method so that 'Enter' keydown will select it
        // !arrowedItem is needed because the keydown event here was intercepting the 'enter'
        // intended to select a arrowedItem
        if (!arrowedItem?.toString() && (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar')) {
          setShowDropdown((prevState) => !prevState);
        }
        break;
      case 'click':
        if (openOnClick || isTouchScreen) setShowDropdown((prevState) => !prevState);
        break;
      case 'mouseenter':
      case 'mouseleave':
        if (openOnHover && !isTouchScreen) setShowDropdown(e.type === 'mouseenter');
        break;
      default:
    }
  };

  const runDynamicPositioning = (): void => {
    cancelStoredTimeouts();
    const { outOfViewport, outOfOverflowParent } = isOutOfView(contentContainerRef?.current, overflowParentRef?.current);
    if (showDropdown && outOfViewport.bottom) { // overflows bottom of viewport
      setOpenDirectionOverride(OpenDirectionOverride.Up);

      // if an overflowParentRef is provided, check if the dropdown will overflow the parent container when it's respositioned to open up
      if (overflowParentRef?.current) {
        const containerOffset = 15; // gap between dropdown and overflow container
        const dropdownWrapperCoords = menuRef.current.getBoundingClientRect();
        const gapBetweenDropdownWrapperAndContainer = dropdownWrapperCoords.top - outOfOverflowParent.bounding.top;
        const dropdownHeight = outOfViewport.bounding.height;
        const willBeOutOfContainerParentTop = gapBetweenDropdownWrapperAndContainer > 0 && dropdownHeight > (gapBetweenDropdownWrapperAndContainer + containerOffset);

        if (willBeOutOfContainerParentTop || outOfOverflowParent?.top) {
          const bottom = `${dropdownWrapperCoords.bottom - (outOfOverflowParent.bounding.top + containerOffset + dropdownHeight)}px`;
          setInlineStyles({ bottom });
        }
      }
    }

    // reset state for dynamic dropdown placement (refs need to be reset so initially rendered dropdown will be out of view)
    if (!showDropdown) {
      const newTimeout = setTimeout(() => {
        setOpenDirectionOverride(null);
        setInlineStyles({});
      }, dropdownTransitionTime + 50); // setTimeout accounts for CSSTransition 'timeout' time, the setTimeout callback should fire after the CSSTransition transition has finished
      setStoredTimeouts((timeouts) => ([...timeouts, newTimeout]));
    }
  };

  const contentContainerClassNames: string = classnames(
    'content-container',
    'dropdown-transitions',
    styleClassNames['content-container'],
    styleClassNames[openDirectionOverride || openDirection], // orients dropdown container relative to wrapper
  );

  const cleanup = (): void => {
    setShowDropdown(false); // close dropdown when unmounting
    cancelStoredTimeouts(); // cancel all outstanding timeouts when unmounting
  };

  useEffect(() => cleanup, []);

  useEffect(() => (
    manageDropdownListener(menuRef, showDropdown, setShowDropdown)
  ), [menuRef, showDropdown, setShowDropdown]);

  useEffect(() => (
    manageKeydownListener(showDropdown, keydownCallback)
  ), [showDropdown, keydownCallback]);

  useEffect(() => {
    if (closeDropdown) {
      setShowDropdown(false); // allows consumer/parent component to close dropdown
      closeCallback();
    }
  }, [closeDropdown]);

  useEffect(() => {
    setIsDropdownOpen(showDropdown);
  }, [showDropdown]);

  useLayoutEffect(() => { // useLayoutEffect runs after render but before useEffect
    if (contentContainerRef.current && openDirection === ListOpenDirections.Down) { // TODO: setup for openDirection values other than 'down'
      runDynamicPositioning();
    }
  }, [showDropdown, overflowParentRef]);

  return (
    <div
      className={classnames(
        className, // user specified class
        `bf-${baseName}__${variantName}`, // base-variant class for post consumption styling
        styleClassNames[variantName], // class for css modules
        {
          [styleClassNames.open]: showDropdown,
          open: showDropdown,
          closed: !showDropdown
        },
      )}
      onMouseEnter={handleOpenAndClose}
      onMouseLeave={handleOpenAndClose}
      ref={menuRef}
    >
      <div
        aria-activedescendant={arrowedItem?.toString()}
        className={classnames(
          'anchor-element-click-container',
          styleClassNames['anchor-element-click-container'],
        )}
        onClick={handleOpenAndClose}
        onKeyDown={handleOpenAndClose}
        role="group"
        tabIndex={0}
      >
        {anchorElement || 'ANCHOR ELEMEMT PLACEHOLDER'}
      </div>
      <CSSTransition
        classNames={dropdownTransition}
        in={showDropdown}
        timeout={dropdownTransitionTime}
        unmountOnExit
      >
        {/* accomodates use case where consumer passes in children that include more content than just list items */}
        {useUlContainer
          ? (
            <ul
              className={contentContainerClassNames}
              ref={contentContainerRef}
              style={inlineStyles}
            >
              {children}
            </ul>
          )
          : (
            <div
              className={contentContainerClassNames}
              ref={contentContainerRef}
              style={inlineStyles}
            >
              {children}
            </div>
          )
        }
      </CSSTransition>
    </div>
  );
};

export default BaseDropdown;
